import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { queriesPayeeRows } from "@query";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";

type Value = string | null;

type Props = {
  value: Value;
  onChange: (value: Value) => void;
  defaultSearchText?: string;
} & Omit<WSSelectSearchProps, "options" | "value" | "onChange">;

export const SelectPayee: React.FC<Props> = ({
  value,
  onChange,
  defaultSearchText,
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState(defaultSearchText ?? "");
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const queryPayeeRows = queriesPayeeRows.searchPayeeRows.usePaginatedQuery({
    filter: {
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  // The selected contractor is read from the same search rows the options come
  // from, so the label of a picked contractor matches the label it had in the
  // list. A `Payee` read would name it off the raw `profile.displayName`
  // instead of the server-resolved `profile.resolvedName` the rows carry.
  const xWingspanAccount = useXWingspanPayerId();
  const querySelectedPayeeRow = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: { id: { eq: value as string } },
      page: { size: 1 },
      xWingspanAccount
    },
    { enabled: !!value }
  );

  const payee = querySelectedPayeeRow.data?.data?.[0];

  useEffect(() => {
    if (!payee) {
      if (value) {
        return;
      }

      setSelectValue(null);
      return;
    }

    const name = selectorSearchRowName(payee);
    const secondaryName = payee.email;

    setSelectValue({
      value: payee.id!,
      label: name,
      labels: [secondaryName].filter(Boolean) as string[],
      avatar: {
        type: "text",
        text: name
      }
    });
  }, [payee, value]);

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayeeRows.flatData ?? []).forEach(payee => {
      const name = selectorSearchRowName(payee);

      options.push({
        value: payee.id ?? "",
        label: name,
        labels: payee.email ? [payee.email] : [],
        avatar: {
          type: "text",
          text: name
        }
      });
    });

    return options;
  }, [queryPayeeRows.flatData]);

  const onReachMenuEnd = useCallback(() => {
    if (queryPayeeRows.hasNextPage) {
      queryPayeeRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayeeRows.hasNextPage]);

  return (
    <WSSelectSearch
      {...otherProps}
      value={selectValue}
      onChange={newOption => {
        if (newOption === null) {
          onChange(null);
          setSelectValue(null);
          return;
        } else if (newOption?.value) {
          onChange(newOption?.value as string);
          setSelectValue(newOption);
        }
      }}
      placeholder={otherProps.placeholder ?? "All contractors"}
      options={options}
      status={queryPayeeRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        (queryPayeeRows.isFetching && !queryPayeeRows.isFetchingNextPage) ||
        querySelectedPayeeRow.isInitialLoading
      }
      onReachMenuEnd={onReachMenuEnd}
    />
  );
};
